import { Article, BreadcrumbList, FAQPage, HowTo, WithContext } from "schema-dts";
import BlogPostScreen from "@components/Screens/BlogPostScreen/BlogPostScreen";
import Template from "@app/Template/Template";
import { PROJECT_NAME } from "@shared/config/GlobalText";
import { configApi } from "@shared/lib/configApi";
import { RouterConfig } from "@shared/lib/routerConfig";
import { getArticleSchema } from "@shared/lib/utils/getArticleSchema";
import { getBreadcrumbBlogPostSchema } from "@shared/lib/utils/getBreadcrumbBlogPostSchema";
import { getFAQSchema } from "@shared/lib/utils/getFAQSchema";
import { getHowToSchema } from "@shared/lib/utils/getHowToSchema";
import { getSchemaByType } from "@shared/lib/utils/getSchemaByType";
import { CustomHead, DEFAULT_OG_IMAGE } from "@shared/ui/CustomHead";
import { SchemaJsonLd } from "@shared/ui/SchemaJsonLd";

export async function getServerSideProps(context: any) {
    const { id } = context.query;
    const res = await fetch(`${process.env.NEXT_PUBLIC_BLOG_URL}posts/?slug=${id}&_embed`);
    const data = await res.json();
    const hasData = !!data && data.length > 0;
    context.res.statusCode = hasData ? 200 : 404;
    return { props: { post: data } };
}

const BlogPage = ({ post }: any) => {
    const tags = post[0]?._embedded["wp:term"]
        ?.flat()
        ?.map((item: any) => {
            if (item.taxonomy === "post_tag") {
                return { slug: item.slug, name: item.name };
            }
        })
        ?.filter((i: string) => i);

    const mark = tags?.filter((item: any) => {
        return item.slug?.includes("mark");
    })[0]?.name;

    const model = tags?.filter((item: any) => {
        return item.slug?.includes("model");
    })[0]?.name;

    const generation = tags?.filter((item: any) => {
        return item.slug?.includes("generation");
    })[0]?.name;

    const aioseo = post[0]?.aioseo_head_json ?? {};
    const postSlug = post[0]?.slug;
    const authorSlug = post[0]?.x_author_slug
        ? `${configApi.API_URL}${RouterConfig.AUTHORS}/${post[0]?.x_author_slug}`
        : `${configApi.API_URL}${RouterConfig.AUTHORS}`;

    const title = aioseo.title ?? post[0]?.title?.rendered;
    const description = aioseo.description ?? "";
    const canonicalUrl = `${configApi.API_PUBLIC_DOMAIN}${RouterConfig.BLOG}/${postSlug}`;
    const ogImageUrl = aioseo["og:image"] ?? DEFAULT_OG_IMAGE;
    const ogLocale = aioseo["og:locale"] ?? "ru_RU";
    const ogSiteName = aioseo["og:site_name"] ?? PROJECT_NAME;

    const graph = aioseo.schema?.["@graph"] ?? [];

    const articleData = getSchemaByType<Article>(graph, "BlogPosting");
    const faqData = getSchemaByType<WithContext<FAQPage>>(graph, "FAQPage");
    const howToData = getSchemaByType<WithContext<HowTo>>(graph, "HowTo");

    const articleSchema = articleData
        ? getArticleSchema({ articleData, graph, canonicalUrl, authorSlug })
        : null;
    const faqSchema = faqData ? getFAQSchema({ faqData, canonicalUrl, title }) : null;
    const howToSchema = howToData ? getHowToSchema(howToData) : null;
    const breadcrumbSchema = getBreadcrumbBlogPostSchema(title, canonicalUrl);

    const schemas = [articleSchema, faqSchema, howToSchema, breadcrumbSchema].filter(
        Boolean
    ) as WithContext<Article | FAQPage | HowTo | BreadcrumbList>[];

    if (post) {
        return (
            <Template bgColor="white">
                <CustomHead
                    title={title}
                    description={description}
                    robots={"index,follow"}
                    canonical={canonicalUrl}
                    openGraph={{
                        title: title,
                        description: description,
                        url: canonicalUrl,
                        type: "article",
                        locale: ogLocale,
                        siteName: ogSiteName,
                        image: {
                            url: ogImageUrl,
                            alt: title
                        }
                    }}
                />
                {schemas.length > 0 && <SchemaJsonLd schemas={schemas} />}
                <BlogPostScreen post={post[0]} searchTerms={{ mark, model, generation }} />
            </Template>
        );
    } else {
        return null;
    }
};

export default BlogPage;